import {
  StageStatus,
  ProcessResultData,
  ApplyResultData,
  PrepareResultData,
} from './types'
import { SummaryTableRow } from '../../SummaryTable'

/**
 * Capitalize the first letter of a string
 */
export const capitalize = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

/**
 * Extract display value from correction values
 */
export const getDisplayValue = (
  value: string | Record<string, unknown>,
  fieldName: string,
): string => {
  if (typeof value === 'string') {
    return value
  }

  // For body fields, extract the 'value' property if it exists
  if (
    fieldName === 'body' &&
    value &&
    typeof value === 'object' &&
    'value' in value
  ) {
    return String(value.value)
  }

  // For other object types, stringify them
  return JSON.stringify(value, null, 2)
}

/**
 * Sanitize HTML for preview by replacing media tags with placeholders
 * This prevents images and large media from loading while preserving text content
 */
export const sanitizeForPreview = (html: string): string => {
  if (!html || typeof html !== 'string') {
    return html
  }

  let sanitized = html

  // Replace img tags with placeholder (preserve alt text if available)
  sanitized = sanitized.replaceAll(
    /<img[^>]*alt=["']([^"']*)["'][^>]*>/gi,
    '<span class="media-placeholder">[Image: $1]</span>',
  )
  sanitized = sanitized.replaceAll(
    /<img[^>]*>/gi,
    '<span class="media-placeholder">[Image]</span>',
  )

  // Replace video tags with placeholder
  sanitized = sanitized.replaceAll(
    /<video[^>]*>[\s\S]*?<\/video>/gi,
    '<span class="media-placeholder">[Video]</span>',
  )

  // Replace audio tags with placeholder
  sanitized = sanitized.replaceAll(
    /<audio[^>]*>[\s\S]*?<\/audio>/gi,
    '<span class="media-placeholder">[Audio]</span>',
  )

  // Replace iframe/embed tags with placeholder
  sanitized = sanitized.replaceAll(
    /<iframe[^>]*>[\s\S]*?<\/iframe>/gi,
    '<span class="media-placeholder">[Embedded Content]</span>',
  )
  sanitized = sanitized.replaceAll(
    /<embed[^>]*>/gi,
    '<span class="media-placeholder">[Embedded Content]</span>',
  )

  // Replace object tags with placeholder
  sanitized = sanitized.replaceAll(
    /<object[^>]*>[\s\S]*?<\/object>/gi,
    '<span class="media-placeholder">[Embedded Object]</span>',
  )

  return sanitized
}

/**
 * Get sanitized display value for preview
 */
export const getSanitizedDisplayValue = (
  value: string | Record<string, unknown>,
  fieldName: string,
): string => {
  const displayValue = getDisplayValue(value, fieldName)
  return sanitizeForPreview(displayValue)
}

// Helper function to build status table rows
export const getStatusTableRows = (
  status: StageStatus,
  detail: string | null,
): SummaryTableRow[] => {
  return [
    {
      cells: ['Status', status.completed ? 'Completed' : 'In Progress'],
    },
    {
      cells: [
        'Jobs',
        `${status.dependant_jobs.completed + status.worker_jobs.completed + status.dependant_jobs.failed + status.worker_jobs.failed}/${status.dependant_jobs.total + status.worker_jobs.total}`,
      ],
    },
    {
      cells: [
        'Failed Jobs',
        status.dependant_jobs.failed + status.worker_jobs.failed,
      ],
    },
    ...(detail ? [{ cells: ['Detail', detail] }] : []),
  ]
}

// Helper function to build prepare result table rows
export const getPrepareResultTableRows = (
  prepareResultData: PrepareResultData,
): SummaryTableRow[] => {
  const prepareResult = prepareResultData.results[0]
  if (!prepareResult) {
    return []
  }
  const stats = prepareResult.result.scanning_stats
  const totalCorrectionJobs = stats.total_correction_jobs_by_name
    ? Object.values(stats.total_correction_jobs_by_name).reduce(
        (sum, count) => sum + count,
        0,
      )
    : 0

  return [
    {
      cells: ['Nodes Scanned', stats.total_nodes_scanned],
    },
    {
      cells: ['Fields to be corrected', stats.total_fields_to_be_corrected],
    },
    ...(stats.total_fields_by_name
      ? Object.entries(stats.total_fields_by_name).map(([fieldName, count]) => {
          return {
            cells: [`${capitalize(fieldName)} types to be corrected`, count],
          }
        })
      : []),
    {
      cells: ['Correction Jobs', totalCorrectionJobs + 1],
    },
  ]
}

// Helper function to build process result table rows
export const getProcessResultTableRows = (
  processResultData: ProcessResultData,
): SummaryTableRow[] => {
  const processResult = processResultData.results[0]
  if (!processResult) {
    return []
  }

  const result = processResult.result
  const usage = result.usage

  // Calculate unique nodes from corrected and unchanged entries
  const uniqueNodeIds = new Set<string>()
  result.corrected?.title?.forEach((entry) => {
    entry.node_ids.forEach((nodeId) => uniqueNodeIds.add(nodeId))
  })
  result.corrected?.description?.forEach((entry) => {
    entry.node_ids.forEach((nodeId) => uniqueNodeIds.add(nodeId))
  })
  result.corrected?.body?.forEach((entry) => {
    entry.node_ids.forEach((nodeId) => uniqueNodeIds.add(nodeId))
  })
  result.unchanged?.title?.forEach((entry) => {
    entry.node_ids.forEach((nodeId) => uniqueNodeIds.add(nodeId))
  })
  result.unchanged?.description?.forEach((entry) => {
    entry.node_ids.forEach((nodeId) => uniqueNodeIds.add(nodeId))
  })
  result.unchanged?.body?.forEach((entry) => {
    entry.node_ids.forEach((nodeId) => uniqueNodeIds.add(nodeId))
  })

  return [
    {
      cells: ['Total AI Correction Jobs', usage.total_processed],
    },
    {
      cells: ['Total Corrected', usage.total_corrected],
    },
    {
      cells: ['Total Unchanged', usage.total_unchanged],
    },
    {
      cells: ['Total Failed', usage.total_failed],
    },
    {
      cells: ['OpenAI Tokens Used', usage.total_tokens.toLocaleString()],
    },
    {
      cells: ['Node Corrections to be Applied', uniqueNodeIds.size],
    },
  ]
}

// Helper function to build apply result table rows
export const getApplyResultTableRows = (
  applyResultData: ApplyResultData,
): SummaryTableRow[] => {
  const applyResult = applyResultData.results[0]
  if (!applyResult) {
    return []
  }
  const { result } = applyResult

  return [
    {
      cells: ['Total Processed', result.total_processed],
    },
    {
      cells: ['Total Successful', result.total_successful],
    },
    {
      cells: ['Total Failed', result.total_failed],
    },
  ]
}
